import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { ScanLine, CheckCircle, XCircle, UserCheck, ShieldCheck, QrCode, ArrowRight, ChevronRight, Eye, Info, Camera, CameraOff, Flashlight, Edit3, LogIn, LogOut } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './GatePass.module.css';

const GatePass = () => {
  const [activeTab, setActiveTab] = useState('enter');
  const [scanPass, setScanPass] = useState('');
  const [activePass, setActivePass] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCameraOn, setIsCameraOn] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    let html5QrCode;
    
    if (activeTab === 'scan' && isCameraOn) {
      html5QrCode = new Html5Qrcode("qr-reader");
      
      const startScanner = async () => {
        try {
          await html5QrCode.start(
            { facingMode: "environment" },
            { fps: 10, qrbox: { width: 250, height: 250 } },
            async (decodedText) => {
              if (html5QrCode.isScanning) {
                await html5QrCode.stop();
                html5QrCode.clear();
                setIsCameraOn(false); // Auto-turn off camera on successful scan
              }
              try {
                const { data } = await api.get(`/security/gatepasses/${decodedText}/verify`);
                setActivePass(data);
              } catch (error) {
                toast.error("Invalid QR Code or not found");
              }
            },
            (errorMessage) => {
              // ignore frame errors
            }
          );
        } catch (err) {
          console.error("Camera error:", err);
          toast.error("Camera permission denied or not available");
          setIsCameraOn(false);
        }
      };
      
      startScanner();
    }

    return () => {
      if (html5QrCode && html5QrCode.isScanning) {
        html5QrCode.stop().then(() => {
          html5QrCode.clear();
        }).catch(console.error);
      }
    };
  }, [activeTab, isCameraOn]);

  const getImageUrl = (url) => {
    if (!url) return 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png';
    if (url.startsWith('http')) return url;
    return `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${url}`;
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/security/gatelogs');
      
      const expandedLogs = [];
      data.forEach(log => {
        // Check-In Event
        expandedLogs.push({
          ...log,
          action: 'CHECK_IN',
          displayTime: log.entryTime || log.createdAt,
          _feedId: `${log._id}-in`
        });
        
        // Check-Out Event
        if (log.exitTime) {
          expandedLogs.push({
            ...log,
            action: 'CHECK_OUT',
            displayTime: log.exitTime,
            _feedId: `${log._id}-out`
          });
        }
      });
      
      expandedLogs.sort((a, b) => new Date(b.displayTime) - new Date(a.displayTime));
      
      setLogs(expandedLogs);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.get(`/security/gatepasses/${scanPass}/verify`);
      setActivePass(data);
    } catch (error) {
      toast.error("Invalid Gate Pass ID or not found");
    }
  };

  const handleEntry = async () => {
    try {
      const response = await api.put(`/security/gatepasses/${activePass._id}/entry`, { method: activeTab === 'scan' ? 'QR' : 'PASS_ID' });
      toast.success(response.data.message || 'Entry Logged');
      setActivePass(null);
      setScanPass('');
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to log entry');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div className={styles.headerTitle}>
          <ShieldCheck size={28} className={styles.shieldIcon} />
          <h1>Gate Pass Verification</h1>
        </div>
        <p>Scan QR code or enter Pass ID to verify resident staff and guests</p>
      </div>

      <div className={styles.tabs}>
        <button 
          className={`${styles.tab} ${activeTab === 'enter' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('enter')}
        >
          <ScanLine size={16} /> Enter Pass ID
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'scan' ? styles.activeTab : ''}`}
          onClick={() => {
            setActiveTab('scan');
            setIsCameraOn(true);
          }}
        >
          <QrCode size={16} /> Scan QR Code
        </button>
      </div>

      {activeTab === 'scan' ? (
        <div className={styles.scanContainer}>
          <div className={styles.scanLeftColumn}>
            <div className={styles.cameraCard}>
              <h3 className={styles.cameraTitle}>Scan QR Code</h3>
              <p className={styles.cameraSubtitle}>Position the QR code within the frame to scan</p>
              
              <div style={{ position: 'relative', width: '100%', maxWidth: '400px', margin: '0 auto', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '250px' }}>
                {isCameraOn ? (
                  <>
                    <div id="qr-reader" style={{ width: '100%' }}></div>
                    <div className={styles.viewfinder} style={{ pointerEvents: 'none' }}>
                      <div className={styles.cornerTL}></div>
                      <div className={styles.cornerTR}></div>
                      <div className={styles.cornerBL}></div>
                      <div className={styles.cornerBR}></div>
                      <div className={styles.scanline}></div>
                    </div>
                  </>
                ) : (
                  <div style={{ color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <CameraOff size={48} opacity={0.5} />
                    <p>Camera is turned off</p>
                    <button 
                      onClick={() => setIsCameraOn(true)}
                      style={{ padding: '8px 16px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500, marginTop: '8px' }}
                    >
                      Turn Camera On
                    </button>
                  </div>
                )}
              </div>
              
              {isCameraOn && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginTop: '16px' }}>
                  <button 
                    onClick={() => setIsCameraOn(false)}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white', cursor: 'pointer', color: '#475569', fontWeight: 500 }}
                  >
                    <CameraOff size={16} /> Turn Off
                  </button>
                </div>
              )}
            </div>

            <div className={styles.tipBanner}>
              <ShieldCheck size={20} />
              <span><strong>Tip:</strong> Ask the resident/visitor to show their QR gate pass on their mobile screen.</span>
            </div>
          </div>

          <div className={styles.scanRightColumn}>
            <div className={styles.manualEntryCard}>
              <div className={styles.manualHeader}>
                <div className={styles.manualIcon}>
                  <Edit3 size={20} />
                </div>
                <div>
                  <h4>Or Enter Pass ID Manually</h4>
                  <p>Enter the 6-character Pass ID</p>
                </div>
              </div>
              <form onSubmit={(e) => {
                e.preventDefault();
                handleScan(e);
              }} className={styles.manualForm}>
                <input 
                  type="text" 
                  placeholder="E.G. GP-2051" 
                  value={scanPass} 
                  onChange={e => setScanPass(e.target.value)} 
                  required
                />
                <button type="submit">Verify Pass <ArrowRight size={16} /></button>
              </form>
            </div>

            <div className={styles.compactListCard}>
              <div className={styles.recentHeader}>
                <h3>Recent Gate Activity</h3>
                <button className={styles.viewAllBtn}>View All <ChevronRight size={14} /></button>
              </div>
              <div className={styles.compactList}>
                {logs.slice(0, 5).map(log => {
                  const isIn = log.action === 'CHECK_IN';
                  const isOut = log.action === 'CHECK_OUT';
                  return (
                    <div className={styles.compactListItem} key={log._feedId}>
                      <div className={`${styles.listIcon} ${isIn ? styles.in : isOut ? styles.out : styles.in}`}>
                        {isIn ? <LogIn size={14} /> : isOut ? <LogOut size={14} /> : <CheckCircle size={14} />}
                      </div>
                      <div className={styles.listAvatarFallback} style={log.resident?.profileImage ? {background: 'transparent'} : {}}>
                        {log.resident?.profileImage ? (
                          <img src={getImageUrl(log.resident.profileImage)} alt="Profile" style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover'}} />
                        ) : (
                          log.resident?.fullName?.charAt(0) || log.gatePass?.name?.charAt(0) || 'U'
                        )}
                      </div>
                      <div className={styles.listInfo}>
                        <h5>{log.resident?.fullName || log.gatePass?.name}</h5>
                        <p>{log.resident?.flatNumber || log.gatePass?.flat || '—'} • {log.gatePass?.vehicleNumber || '—'}</p>
                      </div>
                      <div className={styles.listMeta}>
                        <span className={styles.listTime}>
                          {new Date(log.displayTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span className={`${styles.badgeStatus} ${isIn ? styles.statusCheckIn : isOut ? styles.statusCheckOut : styles.statusCheckIn}`}>
                          {log.action}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.topSection}>
            <div className={styles.leftCard}>
              <div className={styles.scanIconWrapper}>
                <ScanLine size={32} color="#166534" />
              </div>
              <h3>Verify Gate Pass</h3>
              <p>Enter the 6-character Pass ID provided to the visitor</p>
              <form onSubmit={handleScan} className={styles.scanForm}>
                <div className={styles.inputWrapper}>
                  <input 
                    type="text" 
                    placeholder="E.G. GP-2051" 
                    value={scanPass} 
                    onChange={e => setScanPass(e.target.value)} 
                    required
                  />
                  <ScanLine size={20} className={styles.inputIcon} />
                </div>
                <button type="submit">Verify Pass <ArrowRight size={16} /></button>
              </form>
              <div className={styles.infoText}>
                <Info size={14} /> Pass ID format: Letters + Numbers (e.g. GP-2051)
              </div>
            </div>

            <div className={styles.rightCard}>
              <h3 className={styles.howItWorksTitle}>How it works</h3>
              <div className={styles.timeline}>
                <div className={styles.timelineStep}>
                  <div className={styles.stepNumber}>1</div>
                  <div className={styles.stepInfo}>
                    <h4>Enter Pass ID</h4>
                    <p>Enter the 6-character Pass ID given to the visitor</p>
                  </div>
                </div>
                <div className={styles.timelineStep}>
                  <div className={styles.stepNumber}>2</div>
                  <div className={styles.stepInfo}>
                    <h4>Verify Details</h4>
                    <p>System will verify and fetch resident & vehicle details</p>
                  </div>
                </div>
                <div className={styles.timelineStep}>
                  <div className={styles.stepNumber}>3</div>
                  <div className={styles.stepInfo}>
                    <h4>Check-In / Check-Out</h4>
                    <p>Confirm to check-in or check-out the entry</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.recentPasses}>
            <div className={styles.recentHeader}>
              <h3>Recent Gate Activity</h3>
              <button className={styles.viewAllBtn}>View All <ChevronRight size={14} /></button>
            </div>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Pass ID</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Flat No.</th>
                    <th>Vehicle No.</th>
                    <th>Time</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? <tr><td colSpan="8" style={{textAlign: 'center'}}>Loading...</td></tr> : logs.map(log => {
                    const getBadgeClass = (type) => {
                      if(type === 'Resident' || type === 'Permanent Resident') return styles.badgeResident;
                      if(type === 'Staff') return styles.badgeStaff;
                      return styles.badgeDelivery;
                    };
                    
                    const getStatusClass = (action) => {
                      if(action === 'CHECK_IN') return styles.statusCheckIn;
                      if(action === 'CHECK_OUT') return styles.statusCheckOut;
                      return styles.statusExpired;
                    };

                    const getStatusIcon = (action) => {
                      if(action === 'CHECK_IN') return <LogIn size={12} />;
                      if(action === 'CHECK_OUT') return <LogOut size={12} />;
                      return <CheckCircle size={12} />;
                    };

                    const p = log.gatePass;
                    const res = log.resident;

                    return (
                      <tr key={log._feedId}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '24px', height: '24px', background: log.action === 'CHECK_OUT' ? '#eff6ff' : '#f0fdf4', color: log.action === 'CHECK_OUT' ? '#3b82f6' : '#22c55e', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <ArrowRight size={14} />
                            </div>
                            <strong>{p?.passId || 'Manual'}</strong>
                          </div>
                        </td>
                        <td>{res?.fullName || p?.name}</td>
                        <td><span className={`${styles.badgeType} ${getBadgeClass(p?.type)}`}>{p?.type || 'Guest'}</span></td>
                        <td><strong>{res?.flatNumber || p?.flat || '—'}</strong></td>
                        <td>{p?.vehicleNumber || '—'}</td>
                        <td>{new Date(log.displayTime).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                        <td>
                          <span className={`${styles.badgeStatus} ${getStatusClass(log.action)}`}>
                            {getStatusIcon(log.action)} {log.action}
                          </span>
                        </td>
                        <td>
                          <button className={styles.actionBtn} onClick={() => setActivePass(p)}>
                            <Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className={styles.mobileList}>
              {loading ? <p style={{padding: '16px'}}>Loading...</p> : logs.map(log => {
                const getBadgeClass = (type) => {
                  if(type === 'Resident' || type === 'Permanent Resident') return styles.badgeResident;
                  if(type === 'Staff') return styles.badgeStaff;
                  return styles.badgeDelivery;
                };
                
                const getStatusClass = (action) => {
                  if(action === 'CHECK_IN') return styles.statusCheckIn;
                  if(action === 'CHECK_OUT') return styles.statusCheckOut;
                  return styles.statusExpired;
                };

                const getStatusIcon = (action) => {
                  if(action === 'CHECK_IN') return <LogIn size={12} />;
                  if(action === 'CHECK_OUT') return <LogOut size={12} />;
                  return <CheckCircle size={12} />;
                };

                const p = log.gatePass;
                const res = log.resident;

                return (
                  <div key={log._feedId} className={styles.mobileCard}>
                    <div className={styles.mobileCardHeader}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                           <div style={{ width: '20px', height: '20px', background: log.action === 'CHECK_OUT' ? '#eff6ff' : '#f0fdf4', color: log.action === 'CHECK_OUT' ? '#3b82f6' : '#22c55e', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                             <ArrowRight size={12} />
                           </div>
                           <strong style={{ fontSize: '14px', color: '#111827' }}>{res?.fullName || p?.name}</strong>
                        </div>
                        <span className={`${styles.badgeType} ${getBadgeClass(p?.type)}`} style={{ width: 'fit-content' }}>
                          {p?.type || 'Guest'}
                        </span>
                      </div>
                      <span className={`${styles.badgeStatus} ${getStatusClass(log.action)}`}>
                        {getStatusIcon(log.action)} {log.action}
                      </span>
                    </div>
                    <div className={styles.mobileCardMeta}>
                      <div className={styles.metaRow}>
                        <span>Pass ID: <strong>{p?.passId || 'Manual'}</strong></span>
                        <span>Flat: <strong>{res?.flatNumber || p?.flat || '—'}</strong></span>
                      </div>
                      <div className={styles.metaRow}>
                        <span>Vehicle: <strong>{p?.vehicleNumber || '—'}</strong></span>
                        <span>{new Date(log.displayTime).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <div className={styles.metaRow} style={{ marginTop: '4px' }}>
                        <button className={styles.actionBtn} style={{ width: '100%', justifyContent: 'center', background: '#f8fafc', color: '#0f172a', fontWeight: 600, border: '1px solid #e2e8f0', padding: '10px' }} onClick={() => setActivePass(p)}>
                          <Eye size={16} style={{marginRight: '6px'}} /> View Pass Details
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      <Drawer isOpen={!!activePass} onClose={() => setActivePass(null)} title="Pass Details">
        {activePass && (
          <div className={styles.passDetail}>
            <div className={styles.statusBanner} data-status={activePass.status} style={{ backgroundColor: activePass.status === 'Revoked' ? '#fef2f2' : activePass.currentAction === 'CHECK_OUT' ? '#f0fdf4' : '#eff6ff', color: activePass.status === 'Revoked' ? '#b91c1c' : activePass.currentAction === 'CHECK_OUT' ? '#15803d' : '#1d4ed8' }}>
              {activePass.status === 'Revoked' ? <XCircle size={24} /> : 
               activePass.currentAction === 'CHECK_OUT' ? <LogIn size={24} /> :
               <LogOut size={24} />}
              <h2>{activePass.status === 'Revoked' ? 'Pass Revoked' : 
                   activePass.currentAction === 'CHECK_OUT' ? 'Currently Checked-In' :
                   'Ready for Check-In'}</h2>
            </div>
            
            <div className={styles.infoGrid}>
              <div className={styles.infoGroup} style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: '16px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {activePass.resident?.profileImage ? (
                    <img src={getImageUrl(activePass.resident.profileImage)} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 20, color: '#64748b', fontWeight: 600 }}>{(activePass.resident?.fullName || activePass.name).charAt(0)}</span>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>Pass Holder</div>
                  <strong style={{ fontSize: 18 }}>{activePass.resident?.fullName || activePass.name}</strong>
                </div>
              </div>

              <div className={styles.infoGroup}>
                <label>Pass ID</label>
                <strong>{activePass.passId}</strong>
              </div>
              <div className={styles.infoGroup}>
                <label>Visitor Name</label>
                <strong>
                  {activePass.resident?.fullName || activePass.name}
                </strong>
              </div>
              <div className={styles.infoGroup}>
                <label>Type</label>
                <strong>{activePass.type}</strong>
              </div>
              <div className={styles.infoGroup}>
                <label>Visiting Flat</label>
                <strong>{activePass.resident?.flatNumber ? `${activePass.resident.wing}-${activePass.resident.flatNumber}` : activePass.flat}</strong>
              </div>
              {activePass.vehicleNumber && (
                <div className={styles.infoGroup}>
                  <label>Vehicle No.</label>
                  <strong>{activePass.vehicleNumber}</strong>
                </div>
              )}
              <div className={styles.infoGroup}>
                <label>Valid Until</label>
                <strong>{new Date(activePass.validUntil).toLocaleDateString()}</strong>
              </div>
            </div>

            {activePass.status === 'Active' && (
              <div className={styles.actions}>
                <button 
                  className={styles.btnLogEntry} 
                  onClick={handleEntry}
                  style={{ backgroundColor: activePass.currentAction === 'CHECK_OUT' ? '#2563eb' : '#166534' }}
                >
                  {activePass.currentAction === 'CHECK_OUT' ? (
                    <><LogOut size={20} /> Record Check-Out</>
                  ) : (
                    <><LogIn size={20} /> Record Check-In</>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default GatePass;
